import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Box, Chip, IconButton, Alert, CircularProgress,
    TablePagination, useTheme, alpha
} from '@mui/material';
import { Close } from '@mui/icons-material';
import api from '../utils/Api';

const SchoolSmsLogsModal = ({ open, onClose, school }) => {
    const theme = useTheme();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchLogs = useCallback(async () => {
        if (!school?._id) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/super-admin/schools/${school._id}/sms-logs`, {
                params: {
                    page: page + 1,
                    limit: rowsPerPage
                }
            });
            setLogs(res.data.logs || []);
            setTotalCount(res.data.total || 0);
        } catch (err) {
            console.error('Error fetching school SMS logs:', err);
            setError("Failed to fetch SMS logs");
        } finally {
            setLoading(false);
        }
    }, [school?._id, page, rowsPerPage]);

    useEffect(() => {
        if (open && school) {
            setPage(0);
            fetchLogs();
        }
    }, [open, school]);

    useEffect(() => {
        if (open && school) {
            fetchLogs();
        }
    }, [page, rowsPerPage, fetchLogs, open, school]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    if (!school) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">
                        SMS Logs: {school.name}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Recipient</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((log) => (
                                        <TableRow
                                            key={log._id}
                                            sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                                        >
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {formatDate(log.sentAt)}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {log.recipientPhone}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={log.messageType}
                                                    color={log.messageType === 'system' ? 'secondary' : 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 250, maxWidth: 400, wordBreak: 'break-word' }}>
                                                {log.message}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    size="small"
                                                    label={log.status}
                                                    color={
                                                        log.status === 'sent' ? 'success' :
                                                        log.status === 'failed' ? 'error' : 'warning'
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {logs.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    No SMS logs found for this school.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        {logs.length > 0 && (
                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                            />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SchoolSmsLogsModal;
